// @ts-nocheck
import React from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip, Link, IconButton, Tooltip, useTheme } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

type RowType = {
  id: string;
  location: string;
  machineId: string; // ID of the machine
  machineName?: string; // Name of the machine (optional)
  cause: string;
  description: string;
  date: string;
  endDate: string;
  assignee: string;
  poNumber: string;
  serviceNumber: string;
  ticketNumber: string;
  status?: string; // Optional field for status
};

interface ReportsTableProps {
  rows: RowType[];
  onViewReport?: (report: RowType) => void;
}

const ReportsTable: React.FC<ReportsTableProps> = ({ rows, onViewReport }) => {
  const theme = useTheme();

  // Function to handle the display and linking of ticket numbers
  const getTicketDisplayAndLink = (ticketUrl) => {
    try {
      const url = new URL(ticketUrl);
      const pathSegments = url.pathname.split('/'); // Split the path into segments
      const displayPart = pathSegments.pop(); // Get the last segment as the ticket number
      const prefix = pathSegments.pop() || ''; // Get the second to last segment as the prefix, if available
      return {
        displayPart: prefix.toUpperCase() + '/' + displayPart,
        isValid: true
      };
    } catch {
      // If not a valid URL, return the input as is
      return { displayPart: ticketUrl, isValid: false };
    }
  };



  const sortedRows = [...rows].sort((a, b) => {
    // Handle cases where date might be undefined or null
    if (!a.date) return 1;
    if (!b.date) return -1;
    return new Date(b.date) - new Date(a.date);
  });

  const handleViewReport = (report, event) => {
    event.stopPropagation();
    if (onViewReport) {
      onViewReport(report);
    }
  };

  // Use sx prop for custom styling based on theme mode
  const iconStyle = {
    color: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.primary.main // White in dark mode
  };

  // Table header styling for dark mode
  const tableHeadStyle = theme.palette.mode === 'dark' ? {
    backgroundColor: '#424242', // Gray background in dark mode
    '& th': {
      color: '#fff', // White text in dark mode
      fontWeight: 'bold'
    }
  } : {};

  return (
    <TableContainer component={Paper}>
      <Table aria-label="simple table">
        <TableHead sx={tableHeadStyle}>
          <TableRow>
            <TableCell>Location</TableCell>
            <TableCell>Machine</TableCell>
            <TableCell>Cause</TableCell>
            <TableCell>Description</TableCell>
            <TableCell>Start Date</TableCell>
            <TableCell>End Date</TableCell>
            <TableCell>Assignee</TableCell>
            <TableCell>PO Number</TableCell>
            <TableCell>Service Number</TableCell>
            <TableCell>Ticket Number</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedRows.map((report) => {
            const reportStatus = report.endDate ? "Resolved" : report.date ? "In Progress" : "Pending";
            const { displayPart, isValid } = getTicketDisplayAndLink(report.ticketNumber || '');
            return (
              <TableRow key={report.id}>
                <TableCell>{report.location || 'N/A'}</TableCell>
                <TableCell>{report.machineName || report.machineId || 'N/A'}</TableCell>
                <TableCell>{report.cause || 'N/A'}</TableCell>
                <TableCell>
                  <div className="truncate-text">
                    {report.description || 'N/A'}
                  </div>
                </TableCell>
                <TableCell>{report.date || 'N/A'}</TableCell>
                <TableCell>{report.endDate || 'N/A'}</TableCell>
                <TableCell>{report.assignee || 'N/A'}</TableCell>
                <TableCell>{report.poNumber || 'N/A'}</TableCell>
                <TableCell>{report.serviceNumber || 'N/A'}</TableCell>
                <TableCell>
                  {isValid ? (
                    <Link
                      href={report.ticketNumber}
                      target="_blank"
                      rel="noopener"
                      sx={{ color: theme.palette.mode === 'dark' ? '#90caf9' : theme.palette.primary.main }}
                    >
                      {displayPart}
                    </Link>
                  ) : (
                    displayPart || 'N/A'
                  )}
                </TableCell>
                <TableCell>
                  {reportStatus === "In Progress" && <Chip label="In Progress" color="warning" />}
                  {reportStatus === "Resolved" && <Chip label="Resolved" color="success" />}
                  {reportStatus === "Pending" && <Chip label="Pending" />}
                </TableCell>
                <TableCell>
                  <Tooltip title="View Report">
                    <IconButton
                      size="small"
                      onClick={(e) => handleViewReport(report, e)}
                      sx={iconStyle}
                    >
                      <SearchIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ReportsTable;